/**
 * Configuration settings for the Copilot Studio Web App
 * Configuration is loaded at runtime from /config.json for dynamic updates without redeployment
 */

export interface AppConfig {
  // Azure AD App Registration
  auth: {
    clientId: string;
    tenantId: string;
    authority: string;
    redirectUri: string;
  };
  // Copilot Studio Agent
  copilotStudio: {
    environmentId: string;
    agentIdentifier: string; // Schema name from Copilot Studio
  };
  // Azure OpenAI Agent (Azure AI Foundry — new Foundry project + Responses API)
  openaiAgent?: {
    projectEndpoint: string; // e.g. https://aoai-geb-rfpa-dev.services.ai.azure.com/api/projects/rfpassistant
    agentName: string; // Agent name from Azure AI Foundry (e.g. rfpassistant)
    modelName: string; // Model deployment name the agent uses (e.g. gpt-4.1-mini)
  };
  // API Configuration
  api?: {
    baseUrl: string;
  };
  // Observability
  appInsightsConnectionString?: string;
  // Environment indicator (dev, uat, prod) - hidden when prod or undefined
  environment?: 'dev' | 'uat' | 'prod';
}

// Runtime configuration - loaded from config.json
let runtimeConfig: AppConfig | null = null;
let configLoadPromise: Promise<AppConfig> | null = null;

/**
 * Load configuration from /config.json at runtime
 * This allows updating configuration without rebuilding the app
 */
export async function loadConfig(): Promise<AppConfig> {
  // Return cached config if already loaded
  if (runtimeConfig) {
    return runtimeConfig;
  }

  // Return existing promise if loading is in progress
  if (configLoadPromise) {
    return configLoadPromise;
  }

  // Load config from the API endpoint (reads Azure App Settings — never exposed publicly).
  // Falls back to /config.json for local development when the API is not running.
  configLoadPromise = (async () => {
    try {
      let jsonConfig: any = {};

      const apiResponse = await fetch("/api/appconfig");
      if (apiResponse.ok) {
        jsonConfig = await apiResponse.json();
      } else {
        // Local dev fallback: API not available, try static config.json
        console.warn(
          "GetAppConfig API not available, falling back to /config.json",
        );
        const fileResponse = await fetch("/config.json");
        if (!fileResponse.ok) {
          throw new Error(`Failed to load config: ${fileResponse.status}`);
        }
        jsonConfig = await fileResponse.json();
      }

      // Merge with defaults and environment variables (env vars take precedence for local dev)
      runtimeConfig = {
        auth: {
          clientId:
            import.meta.env.VITE_CLIENT_ID ||
            jsonConfig.auth?.clientId ||
            "YOUR_CLIENT_ID",
          tenantId:
            import.meta.env.VITE_TENANT_ID ||
            jsonConfig.auth?.tenantId ||
            "YOUR_TENANT_ID",
          authority:
            import.meta.env.VITE_AUTHORITY ||
            jsonConfig.auth?.authority ||
            "https://login.microsoftonline.com",
          // Use current origin if redirectUri is empty or not set
          redirectUri:
            import.meta.env.VITE_REDIRECT_URI ||
            jsonConfig.auth?.redirectUri ||
            window.location.origin,
        },
        copilotStudio: {
          environmentId:
            import.meta.env.VITE_ENVIRONMENT_ID ||
            jsonConfig.copilotStudio?.environmentId ||
            "YOUR_ENVIRONMENT_ID",
          agentIdentifier:
            import.meta.env.VITE_AGENT_IDENTIFIER ||
            jsonConfig.copilotStudio?.agentIdentifier ||
            "YOUR_AGENT_SCHEMA_NAME",
        },
        openaiAgent: {
          projectEndpoint:
            import.meta.env.VITE_OPENAI_PROJECT_ENDPOINT ||
            jsonConfig.openaiAgent?.projectEndpoint ||
            "",
          agentName:
            import.meta.env.VITE_OPENAI_AGENT_NAME ||
            jsonConfig.openaiAgent?.agentName ||
            "",
          modelName:
            import.meta.env.VITE_OPENAI_MODEL_NAME ||
            jsonConfig.openaiAgent?.modelName ||
            "gpt-4.1-mini",
        },
        api: {
          baseUrl:
            import.meta.env.VITE_API_URL || jsonConfig.api?.baseUrl || "/api",
        },
        appInsightsConnectionString:
          jsonConfig.appInsightsConnectionString || undefined,
        environment: jsonConfig.environment || undefined,
      };

      console.log("Configuration loaded successfully");
      return runtimeConfig;
    } catch (error) {
      console.warn(
        "Failed to load config.json, falling back to environment variables:",
        error,
      );

      // Fallback to environment variables only
      runtimeConfig = {
        auth: {
          clientId: import.meta.env.VITE_CLIENT_ID || "YOUR_CLIENT_ID",
          tenantId: import.meta.env.VITE_TENANT_ID || "YOUR_TENANT_ID",
          authority:
            import.meta.env.VITE_AUTHORITY ||
            "https://login.microsoftonline.com",
          redirectUri:
            import.meta.env.VITE_REDIRECT_URI || window.location.origin,
        },
        copilotStudio: {
          environmentId:
            import.meta.env.VITE_ENVIRONMENT_ID || "YOUR_ENVIRONMENT_ID",
          agentIdentifier:
            import.meta.env.VITE_AGENT_IDENTIFIER || "YOUR_AGENT_SCHEMA_NAME",
        },
        openaiAgent: {
          projectEndpoint: import.meta.env.VITE_OPENAI_PROJECT_ENDPOINT || "",
          agentName: import.meta.env.VITE_OPENAI_AGENT_NAME || "",
          modelName: import.meta.env.VITE_OPENAI_MODEL_NAME || "gpt-4.1-mini",
        },
        api: {
          baseUrl: import.meta.env.VITE_API_URL || "/api",
        },
        appInsightsConnectionString: undefined,
      };
      return runtimeConfig;
    }
  })();

  return configLoadPromise;
}

/**
 * Get the current configuration (must call loadConfig first)
 * Throws if config hasn't been loaded yet
 */
export function getConfig(): AppConfig {
  if (!runtimeConfig) {
    throw new Error("Configuration not loaded. Call loadConfig() first.");
  }
  return runtimeConfig;
}

/**
 * Legacy export for backward compatibility
 * @deprecated Use getConfig() after loadConfig() instead
 */
export const config: AppConfig = new Proxy({} as AppConfig, {
  get(_, prop) {
    if (!runtimeConfig) {
      console.warn("Accessing config before loadConfig() - using defaults");
      return undefined;
    }
    return (runtimeConfig as any)[prop];
  },
});

/**
 * Validate that all required configuration values are set
 */
export function validateConfig(): { valid: boolean; errors: string[] } {
  const cfg = runtimeConfig || ({} as AppConfig);
  const errors: string[] = [];

  if (!cfg.auth?.clientId || cfg.auth.clientId === "YOUR_CLIENT_ID") {
    errors.push("Client ID is not configured");
  }
  if (!cfg.auth?.tenantId || cfg.auth.tenantId === "YOUR_TENANT_ID") {
    errors.push("Tenant ID is not configured");
  }
  if (
    !cfg.copilotStudio?.environmentId ||
    cfg.copilotStudio.environmentId === "YOUR_ENVIRONMENT_ID"
  ) {
    errors.push("Copilot Studio Environment ID is not configured");
  }
  if (
    !cfg.copilotStudio?.agentIdentifier ||
    cfg.copilotStudio.agentIdentifier === "YOUR_AGENT_SCHEMA_NAME"
  ) {
    errors.push("Copilot Studio Agent Schema Name is not configured");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
